import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const ContactScroll = () => {
    const sectionRef = useRef(null);
    const formRef = useRef(null);
    const infoRef = useRef(null);
    const titleRef = useRef(null);

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        message: ''
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = 'Name is required';
        }

        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }

        if (!formData.phone.trim()) {
            newErrors.phone = 'Phone number is required';
        } else if (!/^[\d\s+()-]{10,}$/.test(formData.phone)) {
            newErrors.phone = 'Please enter a valid phone number';
        }

        if (!formData.message.trim()) {
            newErrors.message = 'Message is required';
        } else if (formData.message.trim().length < 10) {
            newErrors.message = 'Message must be at least 10 characters';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (validateForm()) {
            setIsSubmitting(true);
            // Log form data to console
            console.log('Form submitted:', formData);

            // Format message for WhatsApp
            const whatsappMessage = `*New Inquiry from Website*\n\n*Name:* ${formData.name}\n*Email:* ${formData.email}\n*Phone:* ${formData.phone}\n*Message:* ${formData.message}`;

            // WhatsApp URL with phone number and pre-filled message
            const whatsappNumber = '919000329999'; // WhatsApp number without + sign
            const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`;

            // Open WhatsApp in new tab
            window.open(whatsappUrl, '_blank');

            // Reset form after redirect
            setTimeout(() => {
                setIsSubmitting(false);
                setFormData({ name: '', email: '', phone: '', message: '' });
            }, 500);
        }
    };

    useEffect(() => {
        const ctx = gsap.context(() => {
            // Title animation with scale effect
            gsap.fromTo(
                titleRef.current,
                {
                    opacity: 0,
                    y: 50,
                    scale: 0.9,
                },
                {
                    opacity: 1,
                    y: 0,
                    scale: 1,
                    duration: 1,
                    ease: 'power3.out',
                    scrollTrigger: {
                        trigger: sectionRef.current,
                        start: 'top 80%',
                        end: 'top 50%',
                        scrub: 1,
                    },
                }
            );

            // Info section slide in from left
            gsap.fromTo(
                infoRef.current,
                {
                    opacity: 0,
                    x: -100,
                },
                {
                    opacity: 1,
                    x: 0,
                    duration: 1.2,
                    ease: 'power3.out',
                    scrollTrigger: {
                        trigger: sectionRef.current,
                        start: 'top 80%',
                        end: 'top 40%',
                        scrub: 1,
                    },
                }
            );

            // Form slide in from right
            gsap.fromTo(
                formRef.current,
                {
                    opacity: 0,
                    x: 100,
                },
                {
                    opacity: 1,
                    x: 0,
                    duration: 1.2,
                    ease: 'power3.out',
                    scrollTrigger: {
                        trigger: sectionRef.current,
                        start: 'top 80%',
                        end: 'top 40%',
                        scrub: 1,
                    },
                }
            );
        }, sectionRef);

        return () => ctx.revert();
    }, []);

    return (
        <section
            ref={sectionRef}
            id="contact"
            className="py-20 bg-white relative overflow-hidden"
        >
            {/* Decorative background elements */}
            <div className="absolute top-0 left-0 w-96 h-96 bg-primary-red/5 rounded-full -translate-y-1/2 -translate-x-1/2"></div>
            <div className="absolute bottom-0 right-0 w-64 h-64 bg-primary-red/5 rounded-full translate-y-1/2 translate-x-1/2"></div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                {/* Section Header with kinetic typography */}
                <div ref={titleRef} className="text-center mb-16">
                    <h2 className="text-4xl font-bold text-black mb-2">Get In Touch</h2>
                    <p className="text-lg text-primary-red font-semibold mb-6">Swan Global (A Unit of Swan Abroad Education)</p>
                    <p className="text-lg text-gray mb-8">
                        Ready to start your study abroad journey? Contact us today for a free consultation.
                    </p>
                </div>

                <div className="grid lg:grid-cols-2 gap-16">
                    {/* Contact Info with parallax */}
                    <div ref={infoRef}>
                        <div className="space-y-6">
                            <div className="flex items-start gap-5 pb-6 border-b border-lightGray">
                                <div className="w-12 h-12 bg-primary-red rounded-full flex items-center justify-center text-white text-xl flex-shrink-0">
                                    <i className="fas fa-map-marker-alt"></i>
                                </div>
                                <div>
                                    <h4 className="text-lg font-semibold text-black mb-1">Corporate Office</h4>
                                    <p className="text-gray">613, 6th floor, Fairmount Downtown, Kompally, Secunderabad, Hyderabad, Telangana- 500100</p>
                                    <p className="text-gray text-sm mt-1">Phone: +91-9908260456, +91-9000329999</p>
                                    <p className="text-gray text-sm">Email: info@swanglobaledu.com</p>
                                    <p className="text-gray text-sm">Website: www.swanglobaledu.com</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-5 pb-6 border-b border-lightGray">
                                <div className="w-12 h-12 bg-primary-red rounded-full flex items-center justify-center text-white text-xl flex-shrink-0">
                                    <i className="fas fa-map-marker-alt"></i>
                                </div>
                                <div>
                                    <h4 className="text-lg font-semibold text-black mb-1">Branch Office - Godavarikhani</h4>
                                    <p className="text-gray">#21-2-82/A, Shivalayam Rd, Markendaya Colony, Godavarikhani - 505209, Telangana</p>
                                    <p className="text-gray text-sm mt-1">Branch Head: Sandeep Rangu | Ph: +91 99499 78899</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-5 pb-6 border-b border-lightGray">
                                <div className="w-12 h-12 bg-primary-red rounded-full flex items-center justify-center text-white text-xl flex-shrink-0">
                                    <i className="fas fa-phone-alt"></i>
                                </div>
                                <div>
                                    <h4 className="text-lg font-semibold text-black mb-1">Phone</h4>
                                    <p className="text-gray">+91-9908260456 / +91-9000329999</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-5 pb-6 border-b border-lightGray">
                                <div className="w-12 h-12 bg-primary-red rounded-full flex items-center justify-center text-white text-xl flex-shrink-0">
                                    <i className="fab fa-whatsapp"></i>
                                </div>
                                <div>
                                    <h4 className="text-lg font-semibold text-black mb-1">WhatsApp</h4>
                                    <p className="text-gray">+91-9000329999</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-5">
                                <div className="w-12 h-12 bg-primary-red rounded-full flex items-center justify-center text-white text-xl flex-shrink-0">
                                    <i className="fas fa-envelope"></i>
                                </div>
                                <div>
                                    <h4 className="text-lg font-semibold text-black mb-1">Email</h4>
                                    <p className="text-gray">info@swanglobaledu.com</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-5 pt-6 border-t border-lightGray">
                                <div className="w-12 h-12 bg-primary-red rounded-full flex items-center justify-center text-white text-xl flex-shrink-0">
                                    <i className="fas fa-location-arrow"></i>
                                </div>
                                <div>
                                    <h4 className="text-lg font-semibold text-black mb-1">Location Map</h4>
                                    <div className="rounded-xl overflow-hidden border border-gray-200 shadow-sm mt-3">
                                        <iframe
                                            title="Swan Global Office Map"
                                            src="https://www.google.com/maps?q=613%2C%206th%20floor%2C%20Fairmount%20Downtown%2C%20Kompally%2C%20Secunderabad%2C%20Hyderabad%2C%20Telangana-%20500100&output=embed"
                                            className="w-full h-56"
                                            loading="lazy"
                                            referrerPolicy="no-referrer-when-downgrade"
                                        ></iframe>
                                    </div>
                                    <a
                                        href="https://maps.app.goo.gl/D5wvpG9mbaxkU3DDA?g_st=iw"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-2 text-primary-red font-semibold hover:underline mt-3"
                                    >
                                        <i className="fas fa-external-link-alt"></i>
                                        Open in Google Maps
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Contact Form with parallax */}
                    <div ref={formRef} className="bg-lightGray rounded-2xl p-8 shadow-xl">
                        <h3 className="text-2xl font-bold text-black mb-6">Send Us a Message</h3>
                        <form onSubmit={handleSubmit} className="space-y-6" noValidate>
                            <div>
                                <label htmlFor="name" className="block text-sm font-medium text-black mb-2">
                                    Full Name <span className="text-primary-red">*</span>
                                </label>
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    className={`w-full px-4 py-3 rounded-lg border ${errors.name ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-primary-red focus:border-transparent transition-all duration-300`}
                                    placeholder="Enter your full name"
                                    aria-describedby={errors.name ? 'name-error' : undefined}
                                    aria-invalid={errors.name ? 'true' : 'false'}
                                />
                                {errors.name && (
                                    <p id="name-error" className="mt-1 text-sm text-red-500" role="alert">
                                        {errors.name}
                                    </p>
                                )}
                            </div>

                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-black mb-2">
                                    Email Address <span className="text-primary-red">*</span>
                                </label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    className={`w-full px-4 py-3 rounded-lg border ${errors.email ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-primary-red focus:border-transparent transition-all duration-300`}
                                    placeholder="Enter your email address"
                                    aria-describedby={errors.email ? 'email-error' : undefined}
                                    aria-invalid={errors.email ? 'true' : 'false'}
                                />
                                {errors.email && (
                                    <p id="email-error" className="mt-1 text-sm text-red-500" role="alert">
                                        {errors.email}
                                    </p>
                                )}
                            </div>

                            <div>
                                <label htmlFor="phone" className="block text-sm font-medium text-black mb-2">
                                    Phone Number <span className="text-primary-red">*</span>
                                </label>
                                <input
                                    type="tel"
                                    id="phone"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    className={`w-full px-4 py-3 rounded-lg border ${errors.phone ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-primary-red focus:border-transparent transition-all duration-300`}
                                    placeholder="Enter your phone number"
                                    aria-describedby={errors.phone ? 'phone-error' : undefined}
                                    aria-invalid={errors.phone ? 'true' : 'false'}
                                />
                                {errors.phone && (
                                    <p id="phone-error" className="mt-1 text-sm text-red-500" role="alert">
                                        {errors.phone}
                                    </p>
                                )}
                            </div>

                            <div>
                                <label htmlFor="message" className="block text-sm font-medium text-black mb-2">
                                    Message <span className="text-primary-red">*</span>
                                </label>
                                <textarea
                                    id="message"
                                    name="message"
                                    value={formData.message}
                                    onChange={handleChange}
                                    rows={5}
                                    className={`w-full px-4 py-3 rounded-lg border ${errors.message ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-primary-red focus:border-transparent transition-all duration-300 resize-none`}
                                    placeholder="Tell us about your study abroad goals..."
                                    aria-describedby={errors.message ? 'message-error' : undefined}
                                    aria-invalid={errors.message ? 'true' : 'false'}
                                />
                                {errors.message && (
                                    <p id="message-error" className="mt-1 text-sm text-red-500" role="alert">
                                        {errors.message}
                                    </p>
                                )}
                            </div>

                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="w-full bg-primary-red text-white py-4 px-6 rounded-full font-semibold hover:bg-primary-dark transition-all duration-300 hover:shadow-lg hover:shadow-primary-red/30 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {isSubmitting ? (
                                    <>
                                        <i className="fas fa-spinner fa-spin"></i>
                                        Sending...
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-paper-plane"></i>
                                        Send Message
                                    </>
                                )}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ContactScroll;
